import { create } from 'zustand';
import { listRoomStyles, saveRoomStyle, type RoomStyle } from '../api/chat-room-styles';

/**
 * 대화방 꾸미기 상태 (chat-room-styles).
 *
 * 대화 영역(배경)과 정보 패널(고르는 자리)이 같은 값을 봐야 해서 한 곳에 둔다.
 * 서버가 아직 이 창구를 모르면(404) 조용히 빈 채로 둔다 — 지금까지의 화면이 그대로다.
 */
interface ChatRoomStyleState {
  /** roomId → 꾸밈. 없으면 기본. */
  styles: Record<string, { backgroundId: string | null; accentId: string | null }>;
  loaded: boolean;
  load: () => Promise<void>;
  save: (roomId: string, input: { backgroundId?: string | null; accentId?: string | null }) => Promise<void>;
  reset: () => void;
}

export const useChatRoomStyleStore = create<ChatRoomStyleState>((set, get) => ({
  styles: {},
  loaded: false,

  load: async () => {
    if (get().loaded) return;
    try {
      const list: RoomStyle[] = await listRoomStyles();
      const map: ChatRoomStyleState['styles'] = {};
      for (const s of list) map[s.roomId] = { backgroundId: s.backgroundId, accentId: s.accentId };
      set({ styles: map, loaded: true });
    } catch {
      set({ loaded: true });
    }
  },

  save: async (roomId, input) => {
    const cur = get().styles[roomId] ?? { backgroundId: null, accentId: null };
    const next = {
      backgroundId: input.backgroundId !== undefined ? input.backgroundId : cur.backgroundId,
      accentId: input.accentId !== undefined ? input.accentId : cur.accentId,
    };
    // 화면을 먼저 바꾼다 — 색을 고르는 일은 즉시 보여야 고르는 맛이 난다.
    set((s) => ({ styles: { ...s.styles, [roomId]: next } }));
    try {
      await saveRoomStyle(roomId, next);
    } catch {
      // 저장 실패는 되돌린다 — 다음에 열었을 때 달라 보이면 더 혼란스럽다.
      set((s) => ({ styles: { ...s.styles, [roomId]: cur } }));
      throw new Error('꾸밈을 저장하지 못했습니다.');
    }
  },

  reset: () => set({ styles: {}, loaded: false }),
}));

/** 이 방의 꾸밈(없으면 기본). */
export function styleOf(
  styles: ChatRoomStyleState['styles'], roomId: string | null | undefined,
): { backgroundId: string | null; accentId: string | null } {
  if (!roomId) return { backgroundId: null, accentId: null };
  return styles[roomId] ?? { backgroundId: null, accentId: null };
}
